// @givmo/board — a board invitation's link: ONE address, which the officer copies, the mail carries and
// the invitation landing reads (CBG-FIX14's one link format; CBG-SEC2 made the link an invitation,
// D-CBG-16).
//
//   {console origin}{Board mount}/director/redeem?token=<token>
//   {console origin}{Board mount}/director/redeem?token=<token>&packet=<packet version id>
//
// The token rides the QUERY of a fixed page address, so the address names no class and no
// organization: the landing can't know a seat's class before it previews the invitation, and the
// class always comes from the seat. A packet distribution's invitation is the same link with the
// version beside the token: once the member joins, the landing opens that version.
//
// The address is built from the HOST's mount (`/board` in both class consoles), never from a console
// path spelled here, and it is a console PAGE: nothing here names the API's director prefix. The
// backend's mail builds the same bytes from the console's origin (`app/domain/board_director_links.py`);
// the host's `board-director-link.test.tsx` pins the whole address, as the backend pins its own literal.

import { useCallback } from 'react';
import { BOARD_DIRECTOR_PAGES } from '../board-routes';
import { boardPath } from '../board-routing';
import { useBoardBasePath } from '../board-runtime';

export interface DirectorAccessLinkParts {
  /** The console's origin. An officer's own: every class console hosts its own director door. */
  origin: string;
  /** The host's Board mount (`/board` in both class consoles; empty only on the legacy host). */
  basePath: string;
  /** The invitation's token, as the mint or the distribution returned it. */
  token: string;
  /** The packet version a distribution's invitation opens once the member joins. */
  packetId?: string | null;
}

/**
 * The followable link. `URLSearchParams` leaves a base64url token and a UUID untouched, exactly as
 * the backend's `urlencode` does, so the copied link and the mailed one are the same bytes.
 */
export function directorAccessLink({
  origin,
  basePath,
  token,
  packetId,
}: DirectorAccessLinkParts): string {
  const url = new URL(boardPath(basePath, BOARD_DIRECTOR_PAGES.redeem), origin);
  url.searchParams.set('token', token);
  if (typeof packetId === 'string' && packetId.length > 0) url.searchParams.set('packet', packetId);
  return url.toString();
}

/**
 * Build links on THIS console: its own origin and the host's Board mount. An officer's console is
 * the console that hosts their directors' pages, so the link they copy lands where it can be opened.
 */
export function useDirectorAccessLink(): (token: string, packetId?: string | null) => string {
  const basePath = useBoardBasePath();
  return useCallback(
    (token: string, packetId?: string | null) =>
      directorAccessLink({ origin: window.location.origin, basePath, token, packetId }),
    [basePath],
  );
}

/** What an invitation's address carries. */
export interface DirectorRedeemSearch {
  token?: string;
  packet?: string;
}

const PACKET_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * The redeem route's search, read tolerantly — every host's `validateSearch` for the page, so a host
 * cannot drop the packet a link names. A `packet` that is not a version id is ignored, and the
 * director lands on their boards rather than on a page that cannot load.
 */
export function directorRedeemSearch(search: Record<string, unknown>): DirectorRedeemSearch {
  const token =
    typeof search.token === 'string' && search.token.length > 0 ? search.token : undefined;
  const packet =
    typeof search.packet === 'string' && PACKET_ID.test(search.packet) ? search.packet : undefined;
  return {
    ...(token === undefined ? {} : { token }),
    ...(packet === undefined ? {} : { packet }),
  };
}
